/**
 * SendBird Chat SDK utility
 * Provides initialization, connection management, and real-time collections
 *
 * Best practices implemented:
 * - GroupChannelCollection for paginated + real-time channel list
 * - MessageCollection for paginated + real-time messages
 * - Proper event handlers for live updates
 * - Connection state management
 */
import { buildGoUrl, newCardId } from './ctaLinks';

// The Sendbird SDK is large, so it loads in its own chunk the first time chat connects
// (only the Conversations pages connect).
let sdkPromise = null;
const loadSendbirdSdk = () => {
  if (!sdkPromise) {
    sdkPromise = Promise.all([
      import(/* webpackChunkName: "sendbird-chat" */ '@sendbird/chat'),
      import(/* webpackChunkName: "sendbird-chat" */ '@sendbird/chat/groupChannel'),
    ]).then(([chat, groupChannel]) => ({ SendbirdChat: chat.default, ...groupChannel }));
  }
  return sdkPromise;
};

// Channel type constants for custom_type field in SendBird
export const CHANNEL_TYPES = {
  LIVE_ROOM: 'live_room', // Auto-created when listing published
  EPISODE: 'episode', // Auto-created when episode created
  SHOW: 'show', // Auto-created when show published (org)
  TEAM: 'team', // Manually created custom channel
  DM: 'dm', // Direct message between two users
};

/**
 * The Live Room listing a channel belongs to, or null.
 *
 * Live Room channel urls are `live_room_<listingId>`, and the id is also
 * carried in the channel's data payload; prefer the data, fall back to the
 * url so channels created before the field existed still resolve.
 */
export const liveRoomListingIdFromChannel = channel => {
  if (!channel || channel.customType !== CHANNEL_TYPES.LIVE_ROOM) return null;
  const fromData = channel.data?.listingId;
  if (fromData) return fromData;
  const url = channel.channelUrl || channel.url || '';
  const prefix = `${CHANNEL_TYPES.LIVE_ROOM}_`;
  return url.startsWith(prefix) ? url.slice(prefix.length) : null;
};

/**
 * The Live Room listing an *episode* channel belongs to, plus the episode.
 * Episode channel urls are `episode_<listingId>_<episodeId>`.
 */
export const episodeIdsFromChannel = channel => {
  if (!channel || channel.customType !== CHANNEL_TYPES.EPISODE) return null;
  const fromData =
    channel.data?.listingId && channel.data?.episodeId
      ? { listingId: channel.data.listingId, episodeId: channel.data.episodeId }
      : null;
  if (fromData) return fromData;
  const url = channel.channelUrl || channel.url || '';
  const prefix = `${CHANNEL_TYPES.EPISODE}_`;
  if (!url.startsWith(prefix)) return null;
  // Ids are UUIDs, so split on the separator between them rather than on
  // every underscore.
  const rest = url.slice(prefix.length);
  const sep = rest.indexOf('_');
  if (sep < 0) return null;
  return { listingId: rest.slice(0, sep), episodeId: rest.slice(sep + 1) };
};

// Singleton instance
let sendbirdInstance = null;
let connectionStatus = 'disconnected';

// Active collections (for cleanup)
let activeChannelCollection = null;
let activeMessageCollection = null;

/**
 * Get the SendBird App ID from environment
 */
const getAppId = () => {
  const appId = process.env.REACT_APP_SENDBIRD_APP_ID;
  if (!appId) {
    console.warn('SendBird App ID not configured. Set REACT_APP_SENDBIRD_APP_ID in .env');
  }
  return appId;
};

/**
 * Initialize SendBird SDK
 * Call this once when the app starts or user logs in
 *
 * @param {string} userId - The user's unique ID
 * @param {string} accessToken - Session token from server
 * @returns {Promise<SendbirdChat>} - The connected SendBird instance
 */
export const initializeSendbird = async (userId, accessToken) => {
  const appId = getAppId();

  if (!appId) {
    throw new Error('SendBird App ID not configured');
  }

  if (!userId) {
    throw new Error('User ID is required to initialize SendBird');
  }

  // If already connected with same user, return existing instance
  if (sendbirdInstance && connectionStatus === 'connected') {
    const currentUser = sendbirdInstance.currentUser;
    if (currentUser && currentUser.userId === userId) {
      return sendbirdInstance;
    }
    // Different user - disconnect first
    await disconnectSendbird();
  }

  try {
    connectionStatus = 'connecting';

    // Initialize SendBird with group channel module
    const { SendbirdChat, GroupChannelModule } = await loadSendbirdSdk();
    sendbirdInstance = SendbirdChat.init({
      appId,
      modules: [new GroupChannelModule()],
      localCacheEnabled: false,
    });

    // Connect user with access token
    if (accessToken) {
      await sendbirdInstance.connect(userId, accessToken);
    } else {
      // Connect without token (for development/testing)
      await sendbirdInstance.connect(userId);
    }

    connectionStatus = 'connected';

    return sendbirdInstance;
  } catch (error) {
    connectionStatus = 'error';
    console.error('Failed to initialize SendBird:', error);
    throw error;
  }
};

/**
 * Get the current SendBird instance
 * Returns null if not initialized
 *
 * @returns {SendbirdChat|null}
 */
export const getSendbirdInstance = () => {
  return sendbirdInstance;
};

/**
 * Get the current connection status
 *
 * @returns {'disconnected'|'connecting'|'connected'|'error'}
 */
export const getConnectionStatus = () => {
  return connectionStatus;
};

/**
 * Disconnect from SendBird
 * Call this when user logs out
 */
export const disconnectSendbird = async () => {
  // Clean up collections first
  disposeChannelCollection();
  disposeMessageCollection();

  if (sendbirdInstance) {
    try {
      await sendbirdInstance.disconnect();
      console.log('SendBird disconnected');
    } catch (error) {
      console.error('Error disconnecting SendBird:', error);
    }
  }
  sendbirdInstance = null;
  connectionStatus = 'disconnected';
};

/**
 * Update user profile in SendBird
 *
 * @param {Object} params - User profile params
 * @param {string} params.nickname - Display name
 * @param {string} params.profileUrl - Avatar URL
 */
export const updateUserProfile = async ({ nickname, profileUrl }) => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  const params = {};
  if (nickname) params.nickname = nickname;
  if (profileUrl) params.profileUrl = profileUrl;

  await sendbirdInstance.updateCurrentUserInfo(params);
};

// ================ Channel Collection (Best Practice) ================ //

/**
 * Create a GroupChannelCollection for paginated + real-time channel list
 * This is the recommended approach for channel lists in production apps
 *
 * @param {Object} options - Configuration options
 * @param {string[]} options.customTypes - Filter by custom types
 * @param {number} options.limit - Channels per page (default: 20)
 * @param {Object} handlers - Event handlers
 * @param {Function} handlers.onChannelsAdded - Called when new channels appear
 * @param {Function} handlers.onChannelsUpdated - Called when channels are updated
 * @param {Function} handlers.onChannelsDeleted - Called when channels are removed
 * @returns {Promise<{channels: Array, collection: GroupChannelCollection, hasMore: boolean}>}
 */
export const createChannelCollection = async (options = {}, handlers = {}) => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  const { GroupChannelFilter, GroupChannelListOrder } = await loadSendbirdSdk();

  // Dispose previous collection if exists
  disposeChannelCollection();

  const filter = new GroupChannelFilter();

  if (options.customTypes && options.customTypes.length > 0) {
    filter.customTypesFilter = options.customTypes;
  }

  // Include all channels the user is a member of (including empty ones)
  filter.includeEmpty = true;

  const collection = sendbirdInstance.groupChannel.createGroupChannelCollection({
    filter,
    order: GroupChannelListOrder.CHRONOLOGICAL,
    limit: options.limit || 100,
  });

  // Set up event handlers for real-time updates
  collection.setGroupChannelCollectionHandler({
    onChannelsAdded: (context, channels) => {
      console.log('Channels added:', channels.length);
      if (handlers.onChannelsAdded) {
        handlers.onChannelsAdded(channels.map(formatChannel));
      }
    },
    onChannelsUpdated: (context, channels) => {
      console.log('Channels updated:', channels.length);
      if (handlers.onChannelsUpdated) {
        handlers.onChannelsUpdated(channels.map(formatChannel));
      }
    },
    onChannelsDeleted: (context, channelUrls) => {
      console.log('Channels deleted:', channelUrls.length);
      if (handlers.onChannelsDeleted) {
        handlers.onChannelsDeleted(channelUrls);
      }
    },
  });

  // Load initial channels
  const channels = await collection.loadMore();

  activeChannelCollection = collection;

  return {
    channels: channels.map(formatChannel),
    collection,
    hasMore: collection.hasMore,
  };
};

/**
 * Load more channels (pagination)
 * @returns {Promise<{channels: Array, hasMore: boolean}>}
 */
export const loadMoreChannels = async () => {
  if (!activeChannelCollection) {
    throw new Error('No active channel collection');
  }

  if (!activeChannelCollection.hasMore) {
    return { channels: [], hasMore: false };
  }

  const channels = await activeChannelCollection.loadMore();

  return {
    channels: channels.map(formatChannel),
    hasMore: activeChannelCollection.hasMore,
  };
};

/**
 * Dispose the active channel collection
 */
export const disposeChannelCollection = () => {
  if (activeChannelCollection) {
    activeChannelCollection.dispose();
    activeChannelCollection = null;
  }
};

/**
 * Parse channel data safely - handles both string and object formats
 */
const parseChannelData = data => {
  if (!data) return null;
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch (e) {
    console.error('Failed to parse channel data:', e);
    return null;
  }
};

/**
 * Format SendBird GroupChannel to simplified object
 */
const formatChannel = channel => ({
  channelUrl: channel.url,
  name: channel.name,
  coverUrl: channel.coverUrl,
  customType: channel.customType,
  data: parseChannelData(channel.data),
  memberCount: channel.memberCount,
  members:
    channel.members?.map(m => ({
      userId: m.userId,
      nickname: m.nickname,
      profileUrl: m.profileUrl,
      isActive: m.connectionStatus === 'online',
      // 'operator' | 'none' — kept so callers can tell operators apart even when
      // the channel-level `operators` array isn't populated by the SDK query.
      role: m.role,
    })) || [],
  operators:
    channel.operators?.map(op => ({
      userId: op.userId,
      nickname: op.nickname,
      profileUrl: op.profileUrl,
    })) || [],
  // The current user's role on this channel ('operator' | 'none'). This is the
  // reliable operator signal — `operators`/`members[].role` can be empty
  // depending on how the channel was fetched, but myRole is always populated.
  myRole: channel.myRole,
  unreadMessageCount: channel.unreadMessageCount,
  lastMessage: channel.lastMessage
    ? {
        messageId: channel.lastMessage.messageId,
        message: channel.lastMessage.message,
        createdAt: channel.lastMessage.createdAt,
        sender: channel.lastMessage.sender
          ? {
              userId: channel.lastMessage.sender.userId,
              nickname: channel.lastMessage.sender.nickname,
            }
          : null,
      }
    : null,
  createdAt: channel.createdAt,
  // Archive state
  isFrozen: channel.isFrozen || false,
  hiddenState: channel.hiddenState,
  isArchived: channel.isFrozen && channel.hiddenState === 'hidden_prevent_auto_unhide',
  // Visibility
  isPrivate: channel.isPublic === false,
  // Keep raw channel for SDK operations
  _raw: channel,
});

/**
 * Get the raw SDK GroupChannel from a channel object
 * Handles both formatted channels (with _raw) and plain JSON objects (from API)
 *
 * @param {GroupChannel|Object} channel - The channel (raw, formatted, or plain JSON)
 * @returns {Promise<GroupChannel>} - The SDK GroupChannel
 */
const getRawChannel = async channel => {
  if (!channel) {
    throw new Error('Channel is required');
  }

  // If it has _raw, use that
  if (channel._raw) {
    return channel._raw;
  }

  // If it's already an SDK channel (has sendUserMessage method), use it directly
  if (typeof channel.sendUserMessage === 'function') {
    return channel;
  }

  // Otherwise, fetch the SDK channel using the URL
  const channelUrl = channel.channelUrl || channel.url;
  if (!channelUrl) {
    throw new Error('Channel URL is required');
  }

  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  return await sendbirdInstance.groupChannel.getChannel(channelUrl);
};

// ================ Message Collection (Best Practice) ================ //

/**
 * Create a MessageCollection for paginated + real-time messages
 * This is the recommended approach for message lists in production apps
 *
 * @param {GroupChannel} channel - The channel (can be raw or formatted)
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Messages per page (default: 50)
 * @param {Object} handlers - Event handlers
 * @param {Function} handlers.onMessagesAdded - Called when new messages arrive
 * @param {Function} handlers.onMessagesUpdated - Called when messages are edited
 * @param {Function} handlers.onMessagesDeleted - Called when messages are deleted
 * @param {Function} handlers.onHugeGapDetected - Called when there's a gap in messages
 * @returns {Promise<{messages: Array, collection: MessageCollection, hasMore: boolean}>}
 */
export const createMessageCollection = async (channel, options = {}, handlers = {}) => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  const { MessageFilter, MessageCollectionInitPolicy } = await loadSendbirdSdk();

  // Dispose previous collection if exists
  disposeMessageCollection();

  // Get raw channel if formatted object was passed
  const rawChannel = channel._raw || channel;

  // If we only have channelUrl, fetch the channel
  const groupChannel =
    typeof rawChannel === 'string'
      ? await sendbirdInstance.groupChannel.getChannel(rawChannel)
      : rawChannel.url
      ? rawChannel
      : await sendbirdInstance.groupChannel.getChannel(rawChannel.channelUrl);

  const filter = new MessageFilter();

  const collection = groupChannel.createMessageCollection({
    filter,
    startingPoint: Date.now(),
    limit: options.limit || 50,
  });

  // Set up event handlers for real-time updates
  collection.setMessageCollectionHandler({
    onMessagesAdded: (context, channel, messages) => {
      console.log('Messages added:', messages.length, 'context:', context.source);
      if (handlers.onMessagesAdded) {
        handlers.onMessagesAdded(messages.map(formatMessage), context.source);
      }
    },
    onMessagesUpdated: (context, channel, messages) => {
      console.log('Messages updated:', messages.length);
      if (handlers.onMessagesUpdated) {
        handlers.onMessagesUpdated(messages.map(formatMessage));
      }
    },
    onMessagesDeleted: (context, channel, messageIds) => {
      console.log('Messages deleted:', messageIds.length);
      if (handlers.onMessagesDeleted) {
        handlers.onMessagesDeleted(messageIds);
      }
    },
    onHugeGapDetected: () => {
      console.log('Huge gap detected in messages');
      if (handlers.onHugeGapDetected) {
        handlers.onHugeGapDetected();
      }
    },
    onChannelUpdated: (context, channel) => {
      // Channel was updated while viewing messages
      if (handlers.onChannelUpdated) {
        handlers.onChannelUpdated(formatChannel(channel));
      }
    },
  });

  // Initialize collection - this sets up the collection but with startingPoint at now,
  // we need to load previous messages to get historical messages
  await collection.initialize(MessageCollectionInitPolicy.CACHE_AND_REPLACE_BY_API);

  activeMessageCollection = collection;

  // Load previous messages to get the actual message history
  // (since startingPoint is "now" and all messages are in the past)
  let messages = [];
  if (collection.hasPrevious) {
    messages = await collection.loadPrevious();
  }

  // Mark as read
  await groupChannel.markAsRead();

  return {
    messages: messages.map(formatMessage),
    collection,
    hasPrevious: collection.hasPrevious,
    hasNext: collection.hasNext,
    // The live SDK channel (with _raw exposing pinMessage/pinnedMessageIds and
    // the real operator list). The channel list is loaded over the Platform
    // REST API, whose plain objects lack these — callers upgrade the selected
    // channel to this so operator-only features like pinning work.
    channel: formatChannel(groupChannel),
  };
};

/**
 * Load older messages (scroll up pagination)
 * @returns {Promise<{messages: Array, hasPrevious: boolean}>}
 */
export const loadPreviousMessages = async () => {
  if (!activeMessageCollection) {
    throw new Error('No active message collection');
  }

  if (!activeMessageCollection.hasPrevious) {
    return { messages: [], hasPrevious: false };
  }

  const messages = await activeMessageCollection.loadPrevious();

  return {
    messages: messages.map(formatMessage),
    hasPrevious: activeMessageCollection.hasPrevious,
  };
};

/**
 * Load newer messages (if scrolled up and new messages arrived)
 * @returns {Promise<{messages: Array, hasNext: boolean}>}
 */
export const loadNextMessages = async () => {
  if (!activeMessageCollection) {
    throw new Error('No active message collection');
  }

  if (!activeMessageCollection.hasNext) {
    return { messages: [], hasNext: false };
  }

  const messages = await activeMessageCollection.loadNext();

  return {
    messages: messages.map(formatMessage),
    hasNext: activeMessageCollection.hasNext,
  };
};

/**
 * Dispose the active message collection
 */
export const disposeMessageCollection = () => {
  if (activeMessageCollection) {
    activeMessageCollection.dispose();
    activeMessageCollection = null;
  }
};

/**
 * Format SendBird message to simplified object
 */
const formatMessage = msg => {
  // Parse custom data
  let parsedData = null;
  try {
    parsedData = msg.data ? JSON.parse(msg.data) : null;
  } catch (e) {
    parsedData = null;
  }

  // Extract files array (support both new 'files' array and legacy single 'file')
  let files = null;
  if (parsedData?.files && parsedData.files.length > 0) {
    // New format: array of files
    files = parsedData.files;
  } else if (msg.url) {
    // SendBird native file message
    files = [
      {
        url: msg.url,
        name: msg.name || 'File',
        type: msg.type || 'application/octet-stream',
        size: msg.size || 0,
      },
    ];
  } else if (parsedData?.file) {
    // Legacy single file format
    files = [parsedData.file];
  }

  // Extract poll info from data
  const poll = parsedData?.poll || null;

  // Extract inline episode-card info from data (see sendEpisodeMessage)
  const episode = parsedData?.episode || null;

  // Call-to-action card (see sendCtaMessage)
  const cta = parsedData?.cta?.url ? parsedData.cta : null;

  return {
    messageId: msg.messageId,
    message: msg.message,
    createdAt: msg.createdAt,
    updatedAt: msg.updatedAt,
    sender: msg.sender
      ? {
          userId: msg.sender.userId,
          nickname: msg.sender.nickname,
          profileUrl: msg.sender.profileUrl,
        }
      : null,
    parentMessageId: msg.parentMessageId,
    threadInfo: msg.threadInfo
      ? {
          replyCount: msg.threadInfo.replyCount,
          lastRepliedAt: msg.threadInfo.lastRepliedAt,
          mostRepliedUsers: msg.threadInfo.mostRepliedUsers || [],
        }
      : null,
    data: parsedData,
    messageType: msg.messageType,
    files, // Array of files (or null)
    poll,
    episode,
    cta,
    // Slack-style message reactions: [{ key, userIds }]. SendBird's
    // MessageCollection auto-applies reaction events and re-emits the message
    // via onMessagesUpdated, so these stay live without a dedicated handler.
    reactions: Array.isArray(msg.reactions)
      ? msg.reactions.map(r => ({ key: r.key, userIds: r.userIds || [] }))
      : [],
    // Keep raw message for SDK operations
    _raw: msg,
  };
};

/**
 * Toggle a Slack-style emoji reaction on a message.
 * Reactions must be enabled for the SendBird application (Dashboard →
 * Settings → Chat → Reactions). The MessageCollection handles the live
 * update, so callers only need to fire-and-forget these.
 *
 * @param {GroupChannel|Object} channel - raw or formatted channel
 * @param {Object} message - formatted message (keeps _raw SDK message)
 * @param {string} key - emoji reaction key (the emoji character)
 */
export const addReaction = async (channel, message, key) => {
  const rawChannel = await getRawChannel(channel);
  const rawMessage = message?._raw || message;
  if (!rawMessage) throw new Error('Message is required');
  return rawChannel.addReaction(rawMessage, key);
};

export const deleteReaction = async (channel, message, key) => {
  const rawChannel = await getRawChannel(channel);
  const rawMessage = message?._raw || message;
  if (!rawMessage) throw new Error('Message is required');
  return rawChannel.deleteReaction(rawMessage, key);
};

/**
 * Compute the message with a reaction toggled for `userId`, without touching
 * SendBird. Used for optimistic UI: apply this immediately, fire the real
 * add/deleteReaction in the background, and let the MessageCollection's
 * onMessagesUpdated reconcile to the server truth. Keeps `_raw` intact.
 */
export const toggleReactionLocally = (message, key, userId) => {
  const reactions = Array.isArray(message?.reactions) ? message.reactions : [];
  const idx = reactions.findIndex(r => r.key === key);
  let next;
  if (idx >= 0) {
    const r = reactions[idx];
    const mine = (r.userIds || []).includes(userId);
    const userIds = mine
      ? (r.userIds || []).filter(u => u !== userId)
      : [...(r.userIds || []), userId];
    next = [...reactions];
    if (userIds.length === 0) next.splice(idx, 1);
    else next[idx] = { ...r, userIds };
  } else {
    next = [...reactions, { key, userIds: [userId] }];
  }
  return { ...message, reactions: next };
};

// ================ Direct Channel/Message Operations ================ //

/**
 * Get a specific channel by URL
 *
 * @param {string} channelUrl - The channel URL
 * @returns {Promise<GroupChannel>}
 */
export const getChannel = async channelUrl => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  const channel = await sendbirdInstance.groupChannel.getChannel(channelUrl);
  return formatChannel(channel);
};

/**
 * Load messages from a channel (simple approach without collection)
 * Use createMessageCollection for production - this is for simple cases
 *
 * @param {GroupChannel} channel - The channel
 * @param {Object} options - Options
 * @returns {Promise<BaseMessage[]>}
 */
export const loadMessages = async (channel, options = {}) => {
  const rawChannel = channel._raw || channel;

  if (!rawChannel) {
    throw new Error('Channel is required');
  }

  const params = {
    prevResultSize: options.limit || 50,
    nextResultSize: 0,
    isInclusive: true,
    reverse: false,
  };

  const timestamp = options.timestamp || Date.now();
  const messages = await rawChannel.getMessagesByTimestamp(timestamp, params);

  return messages.map(formatMessage);
};

/**
 * Send a message to a channel (text, files, or both - Slack style)
 *
 * @param {GroupChannel|Object} channel - The channel (raw or formatted)
 * @param {string} messageText - Message text (optional if files provided)
 * @param {Object} options - Optional parameters
 * @param {Array} options.files - Array of file objects with url, name, type, size
 * @returns {Promise<UserMessage>}
 */
export const sendMessage = async (channel, messageText, options = {}) => {
  const { files = [] } = options;
  const rawChannel = await getRawChannel(channel);

  // Build message data
  const data = {};
  if (files.length > 0) {
    data.files = files.map(f => ({
      url: f.url,
      name: f.name,
      type: f.type,
      size: f.size,
    }));
  }

  // Determine message text
  let displayMessage = messageText || '';
  if (!displayMessage && files.length > 0) {
    // Fallback text for clients that don't parse data
    displayMessage =
      files.length === 1 ? `Shared a file: ${files[0].name}` : `Shared ${files.length} files`;
  }

  const params = {
    message: displayMessage,
  };

  if (Object.keys(data).length > 0) {
    params.data = JSON.stringify(data);
  }

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);

    messageHandler.onSucceeded(sentMessage => {
      resolve(formatMessage(sentMessage));
    });

    messageHandler.onFailed(error => {
      reject(error);
    });
  });
};

/**
 * Send a reply to a message (thread)
 *
 * @param {GroupChannel|Object} channel - The channel
 * @param {number} parentMessageId - Parent message ID
 * @param {string} messageText - Reply text
 * @param {Array} files - Optional files to attach
 * @returns {Promise<UserMessage>}
 */
export const sendReply = async (channel, parentMessageId, messageText, files = []) => {
  const rawChannel = await getRawChannel(channel);

  // Build message data
  const data = {};
  if (files.length > 0) {
    data.files = files.map(f => ({
      url: f.url,
      name: f.name,
      type: f.type,
      size: f.size,
    }));
  }

  // Determine message text
  let displayMessage = messageText || '';
  if (!displayMessage && files.length > 0) {
    displayMessage =
      files.length === 1 ? `Shared a file: ${files[0].name}` : `Shared ${files.length} files`;
  }

  const params = {
    message: displayMessage,
    parentMessageId,
    isReplyToChannel: false,
  };

  if (Object.keys(data).length > 0) {
    params.data = JSON.stringify(data);
  }

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);

    messageHandler.onSucceeded(sentMessage => {
      resolve(formatMessage(sentMessage));
    });

    messageHandler.onFailed(error => {
      reject(error);
    });
  });
};

/**
 * Get thread replies for a message
 *
 * @param {GroupChannel} channel - The channel
 * @param {number} parentMessageId - Parent message ID
 * @param {Object} options - Query options
 * @returns {Promise<UserMessage[]>}
 */
export const getThreadReplies = async (channel, parentMessageId, options = {}) => {
  try {
    const rawChannel = await getRawChannel(channel);

    if (!rawChannel) {
      return [];
    }

    // Ensure parentMessageId is a number for SendBird API
    const numericParentMessageId =
      typeof parentMessageId === 'string' ? parseInt(parentMessageId, 10) : parentMessageId;

    // First, get the parent message with its raw SDK object
    const parentParams = {
      prevResultSize: 0,
      nextResultSize: 0,
      isInclusive: true,
    };

    let parentMessage = null;
    try {
      const parentMessages = await rawChannel.getMessagesByMessageId(
        numericParentMessageId,
        parentParams
      );
      parentMessage = parentMessages.find(m => m.messageId === numericParentMessageId);
    } catch (parentError) {
      console.warn('Could not fetch parent message:', parentError);
    }

    // Try getThreadedMessagesByTimestamp if parent message has it
    if (parentMessage && typeof parentMessage.getThreadedMessagesByTimestamp === 'function') {
      try {
        const threadParams = {
          prevResultSize: 50,
          nextResultSize: 50,
          isInclusive: true,
        };

        const result = await parentMessage.getThreadedMessagesByTimestamp(Date.now(), threadParams);
        const threadedMessages = result.threadedMessages || result || [];

        return threadedMessages.map(formatMessage);
      } catch (threadError) {
        console.warn('getThreadedMessagesByTimestamp failed:', threadError);
      }
    }

    // Fallback: Load recent messages and filter for replies
    try {
      const allParams = {
        prevResultSize: 200,
        nextResultSize: 0,
        isInclusive: true,
      };

      const allMessages = await rawChannel.getMessagesByTimestamp(Date.now(), allParams);

      const replies = allMessages.filter(msg => {
        if (!msg.parentMessageId) return false;
        return msg.parentMessageId === numericParentMessageId;
      });

      return replies.map(formatMessage);
    } catch (fallbackError) {
      console.warn('Fallback thread fetch failed:', fallbackError);
    }

    return [];
  } catch (error) {
    console.error('Error in getThreadReplies:', error);
    return [];
  }
};

/**
 * Mark channel as read
 *
 * @param {GroupChannel|Object} channel - The channel
 */
export const markAsRead = async channel => {
  if (!channel) return;

  try {
    const rawChannel = await getRawChannel(channel);
    await rawChannel.markAsRead();
  } catch (error) {
    console.error('Error marking channel as read:', error);
  }
};

/**
 * Get unread message count for current user
 *
 * @returns {Promise<number>}
 */
export const getTotalUnreadCount = async () => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    return 0;
  }

  try {
    return await sendbirdInstance.groupChannel.getTotalUnreadMessageCount();
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Register push token for notifications
 *
 * @param {string} token - FCM or APNS token
 * @param {string} type - 'gcm' or 'apns'
 */
export const registerPushToken = async (token, type = 'gcm') => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  if (type === 'gcm') {
    await sendbirdInstance.registerGCMPushTokenForCurrentUser(token);
  } else {
    await sendbirdInstance.registerAPNSPushTokenForCurrentUser(token);
  }
};

// ================ Typing Indicators ================ //

/**
 * Start typing indicator
 * @param {GroupChannel|Object} channel - The channel
 */
export const startTyping = channel => {
  const rawChannel = channel._raw || channel;
  if (rawChannel) {
    rawChannel.startTyping();
  }
};

/**
 * End typing indicator
 * @param {GroupChannel|Object} channel - The channel
 */
export const endTyping = channel => {
  const rawChannel = channel._raw || channel;
  if (rawChannel) {
    rawChannel.endTyping();
  }
};

// ================ Channel Archive/Delete (Slack-style) ================ //

/**
 * Archive a channel (Slack-style)
 * - Freezes the channel (no new messages except from operators)
 * - Sends a system message that channel was archived (via server API)
 * - Channel stays visible but read-only
 *
 * @param {GroupChannel|Object|string} channel - The channel or channel URL
 * @param {Function} sendAdminMessageFn - Function to send admin message via server API
 * @returns {Promise<Object>} - The archived channel
 */
export const archiveChannel = async (channel, sendAdminMessageFn) => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  // Get the raw channel
  let rawChannel;
  if (typeof channel === 'string') {
    rawChannel = await sendbirdInstance.groupChannel.getChannel(channel);
  } else {
    rawChannel = channel._raw || channel;
  }

  if (!rawChannel) {
    throw new Error('Channel is required');
  }

  try {
    // 1. Freeze the channel to prevent new messages
    await rawChannel.freeze();

    // 2. Send admin message that channel was archived (via server API)
    let adminMessage = null;
    if (sendAdminMessageFn) {
      try {
        const actorId = sendbirdInstance.currentUser?.userId;
        const messageData = { action: 'channel_archived', actorId };
        const response = await sendAdminMessageFn({
          channelUrl: rawChannel.url,
          message: 'This channel has been archived',
          data: messageData,
        });
        // Format the admin message for the UI. Keep `data` so the person who
        // archived it (actorId === them) can filter their own notice out.
        if (response?.message) {
          adminMessage = {
            messageId: response.message.message_id,
            message: response.message.message,
            createdAt: response.message.created_at,
            messageType: 'admin',
            data: messageData,
          };
        }
      } catch (msgError) {
        // Log but don't fail the archive if message fails
        console.error('Failed to send archive notification:', msgError);
      }
    }

    // Note: We don't hide the channel so messages remain accessible
    // The channel stays visible in the list but is read-only

    console.log('Channel archived:', rawChannel.url);

    // Refresh channel data to get updated isFrozen state
    await rawChannel.refresh();
    return { channel: formatChannel(rawChannel), adminMessage };
  } catch (error) {
    console.error('Error archiving channel:', error);
    throw error;
  }
};

/**
 * Unarchive a channel
 * - Unfreezes the channel
 * - Sends a system message that channel was unarchived (via server API)
 *
 * @param {GroupChannel|Object|string} channel - The channel or channel URL
 * @param {Function} sendAdminMessageFn - Function to send admin message via server API
 * @returns {Promise<Object>} - The unarchived channel
 */
export const unarchiveChannel = async (channel, sendAdminMessageFn) => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  // Get the raw channel
  let rawChannel;
  if (typeof channel === 'string') {
    rawChannel = await sendbirdInstance.groupChannel.getChannel(channel);
  } else {
    rawChannel = channel._raw || channel;
  }

  if (!rawChannel) {
    throw new Error('Channel is required');
  }

  try {
    // 1. Unfreeze the channel
    await rawChannel.unfreeze();

    // 2. Send admin message that channel was unarchived (via server API)
    let adminMessage = null;
    if (sendAdminMessageFn) {
      try {
        const actorId = sendbirdInstance.currentUser?.userId;
        const messageData = { action: 'channel_unarchived', actorId };
        const response = await sendAdminMessageFn({
          channelUrl: rawChannel.url,
          message: 'This channel has been unarchived',
          data: messageData,
        });
        // Format the admin message for the UI. Keep `data` so the actor can
        // filter their own notice out.
        if (response?.message) {
          adminMessage = {
            messageId: response.message.message_id,
            message: response.message.message,
            createdAt: response.message.created_at,
            messageType: 'admin',
            data: messageData,
          };
        }
      } catch (msgError) {
        // Log but don't fail the unarchive if message fails
        console.error('Failed to send unarchive notification:', msgError);
      }
    }

    console.log('Channel unarchived:', rawChannel.url);

    // Refresh channel data to get updated isFrozen state
    await rawChannel.refresh();
    return { channel: formatChannel(rawChannel), adminMessage };
  } catch (error) {
    console.error('Error unarchiving channel:', error);
    throw error;
  }
};

/**
 * Delete a channel permanently
 * This action cannot be undone - all messages will be lost
 *
 * @param {GroupChannel|Object|string} channel - The channel or channel URL
 * @returns {Promise<void>}
 */
/**
 * Leave a group channel. Removes the current user from the channel
 * without deleting it for others. Useful for members who want out of a
 * channel they don't own.
 */
export const leaveChannel = async channel => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  const rawChannel =
    typeof channel === 'string'
      ? await sendbirdInstance.groupChannel.getChannel(channel)
      : channel._raw || channel;

  if (!rawChannel) {
    throw new Error('Channel is required');
  }

  try {
    await rawChannel.leave();
    console.log('Left channel:', rawChannel.url);
  } catch (error) {
    console.error('Error leaving channel:', error);
    throw error;
  }
};

export const deleteChannel = async channel => {
  if (!sendbirdInstance || connectionStatus !== 'connected') {
    throw new Error('SendBird not connected');
  }

  // Get the raw channel
  let rawChannel;
  if (typeof channel === 'string') {
    rawChannel = await sendbirdInstance.groupChannel.getChannel(channel);
  } else {
    rawChannel = channel._raw || channel;
  }

  if (!rawChannel) {
    throw new Error('Channel is required');
  }

  try {
    await rawChannel.delete();
    console.log('Channel deleted:', rawChannel.url);
  } catch (error) {
    console.error('Error deleting channel:', error);
    throw error;
  }
};

/**
 * Check if a channel is frozen (archived)
 *
 * @param {GroupChannel|Object} channel - The channel
 * @returns {boolean}
 */
export const isChannelFrozen = channel => {
  const rawChannel = channel._raw || channel;
  return rawChannel?.isFrozen || false;
};

// ================ File Messages ================ //

/**
 * Send a file message with a URL (after uploading to Google Storage)
 *
 * @param {GroupChannel|Object} channel - The channel
 * @param {Object} fileData - File data
 * @param {string} fileData.url - Public URL of the uploaded file
 * @param {string} fileData.name - Original file name
 * @param {string} fileData.type - MIME type
 * @param {number} fileData.size - File size in bytes
 * @param {string} messageText - Optional message text
 * @returns {Promise<UserMessage>}
 */
export const sendFileMessage = async (channel, fileData, messageText = '') => {
  const rawChannel = await getRawChannel(channel);

  const data = {
    file: {
      url: fileData.url,
      name: fileData.name,
      type: fileData.type,
      size: fileData.size,
    },
  };

  const params = {
    message: messageText || `Shared a file: ${fileData.name}`,
    data: JSON.stringify(data),
  };

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);

    messageHandler.onSucceeded(sentMessage => {
      resolve(formatMessage(sentMessage));
    });

    messageHandler.onFailed(error => {
      reject(error);
    });
  });
};

// ================ Episode Card Messages ================ //

/**
 * Send an inline "episode card" message — a rich card referencing one of the
 * creator's scheduled episodes (Live Room availability exception). Mirrors
 * sendPollMessage: the card data lives in the message `data`, and clients
 * render it via the EpisodeMessage component.
 *
 * @param {GroupChannel|Object} channel
 * @param {Object} episode - { id, listingId, listingSlug, title, start, end, accessTier }
 * @returns {Promise<UserMessage>}
 */
export const sendEpisodeMessage = async (channel, episode) => {
  const rawChannel = await getRawChannel(channel);

  const data = {
    episode: {
      id: episode.id,
      listingId: episode.listingId,
      listingSlug: episode.listingSlug || null,
      title: episode.title || '',
      start: episode.start || null,
      end: episode.end || null,
      accessTier: episode.accessTier || null,
    },
  };

  const params = {
    message: `Episode: ${episode.title || ''}`.trim(),
    data: JSON.stringify(data),
  };

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);
    messageHandler.onSucceeded(sentMessage => resolve(formatMessage(sentMessage)));
    messageHandler.onFailed(error => reject(error));
  });
};

// ================ Call-to-action Messages ================ //

/**
 * Send a call-to-action card (CTA build plan, step 5). Same pattern as the
 * episode card: the card lives in the message `data`. Its button is a /go/
 * link with src=chat and the card's own id (m=), so Insights counts its clicks
 * (server/go.js, api-util/clickLog.js).
 *
 * @param {GroupChannel|Object} channel
 * @param {Object} cta - { action, listingId, episodeId, title, start, label } from the picker
 */
export const sendCtaMessage = async (channel, cta) => {
  const rawChannel = await getRawChannel(channel);
  const id = newCardId();
  const data = {
    cta: {
      id,
      action: cta.action,
      listingId: cta.listingId || null,
      episodeId: cta.episodeId || null,
      title: cta.title || null,
      start: cta.start || null,
      label: cta.label,
      url: buildGoUrl(cta, { src: 'chat', m: id }),
    },
  };
  const params = { message: cta.label, data: JSON.stringify(data) };

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);
    messageHandler.onSucceeded(sentMessage => resolve(formatMessage(sentMessage)));
    messageHandler.onFailed(error => reject(error));
  });
};

// ================ Poll Messages ================ //

/**
 * Whether a poll is closed to new votes — either closed early by an
 * operator/author (closedAt) or past its auto-close time (expiresAt).
 * @param {Object} poll - The poll object from message data
 * @returns {boolean}
 */
export const pollIsClosed = poll => {
  if (!poll) return false;
  if (poll.closedAt) return true;
  return !!poll.expiresAt && Date.now() >= poll.expiresAt;
};

/**
 * Send a poll message
 *
 * @param {GroupChannel|Object} channel - The channel
 * @param {Object} pollData - Poll data
 * @param {string} pollData.question - The poll question
 * @param {string[]} pollData.options - Array of option strings
 * @returns {Promise<UserMessage>}
 */
export const sendPollMessage = async (channel, pollData) => {
  const rawChannel = await getRawChannel(channel);

  const pollId = `poll_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  const createdAt = Date.now();
  // Optional auto-close: pollData.durationMs is the selected "Close poll after"
  // window in ms (null = No expiry). We store an absolute expiresAt so every
  // client can decide "closed" without a shared clock.
  const durationMs = pollData.durationMs != null ? pollData.durationMs : null;

  const data = {
    poll: {
      id: pollId,
      question: pollData.question,
      options: pollData.options,
      votes: {}, // { optionIndex: [userId1, userId2, ...] }
      createdAt,
      expiresAt: durationMs ? createdAt + durationMs : null,
      closedAt: null, // set when an operator/author closes the poll early
      // Anonymous voting (default true). The card never displays voter
      // identities regardless; this flag records intent for the non-anonymous
      // voter-reveal model handled by the idea-validation ticket.
      anonymous: pollData.anonymous !== false,
    },
  };

  const params = {
    message: `Poll: ${pollData.question}`,
    data: JSON.stringify(data),
  };

  return new Promise((resolve, reject) => {
    const messageHandler = rawChannel.sendUserMessage(params);

    messageHandler.onSucceeded(sentMessage => {
      resolve(formatMessage(sentMessage));
    });

    messageHandler.onFailed(error => {
      reject(error);
    });
  });
};

/**
 * Vote on a poll (supports changing vote and removing vote)
 * - Click new option: adds vote
 * - Click different option: changes vote
 * - Click same option: removes vote
 *
 * @param {GroupChannel|Object} channel - The channel
 * @param {number} messageId - The poll message ID
 * @param {number} optionIndex - The option index to vote for
 * @param {string} userId - The voting user's ID
 * @returns {Promise<UserMessage>}
 */
export const voteOnPoll = async (channel, messageId, optionIndex, userId) => {
  const rawChannel = await getRawChannel(channel);

  // Get the message to update
  const params = {
    messageId,
    prevResultSize: 0,
    nextResultSize: 0,
    isInclusive: true,
  };

  const messages = await rawChannel.getMessagesByMessageId(messageId, params);
  const message = messages.find(m => m.messageId === messageId);

  if (!message) {
    throw new Error('Poll message not found');
  }

  // Parse existing data
  let data;
  try {
    data = JSON.parse(message.data);
  } catch (e) {
    throw new Error('Invalid poll data');
  }

  if (!data.poll) {
    throw new Error('Message is not a poll');
  }

  // Reject votes once the poll has closed (early close or expiry).
  if (pollIsClosed(data.poll)) {
    throw new Error('Poll is closed');
  }

  const votes = data.poll.votes || {};
  const optionKey = optionIndex.toString();

  // Find if user already voted and for which option
  let previousVoteKey = null;
  for (const [key, voterIds] of Object.entries(votes)) {
    if (voterIds.includes(userId)) {
      previousVoteKey = key;
      break;
    }
  }

  if (previousVoteKey === optionKey) {
    // Clicking same option - remove vote
    votes[optionKey] = votes[optionKey].filter(id => id !== userId);
  } else {
    // Remove from previous option if exists
    if (previousVoteKey !== null) {
      votes[previousVoteKey] = votes[previousVoteKey].filter(id => id !== userId);
    }
    // Add to new option
    if (!votes[optionKey]) {
      votes[optionKey] = [];
    }
    votes[optionKey].push(userId);
  }

  // Update message data
  data.poll.votes = votes;

  // Update the message - SendBird v4 API: updateUserMessage(messageId, params)
  const updateParams = {
    data: JSON.stringify(data),
  };

  return new Promise((resolve, reject) => {
    rawChannel
      .updateUserMessage(messageId, updateParams)
      .then(updatedMessage => {
        resolve(formatMessage(updatedMessage));
      })
      .catch(reject);
  });
};

/**
 * Close a poll early (operator/author action). Stamps closedAt so no further
 * votes are accepted and clients render the final results.
 *
 * @param {GroupChannel|Object} channel - The channel
 * @param {number} messageId - The poll message ID
 * @returns {Promise<UserMessage>}
 */
export const closePoll = async (channel, messageId) => {
  const rawChannel = await getRawChannel(channel);

  const messages = await rawChannel.getMessagesByMessageId(messageId, {
    prevResultSize: 0,
    nextResultSize: 0,
    isInclusive: true,
  });
  const message = messages.find(m => m.messageId === messageId);
  if (!message) {
    throw new Error('Poll message not found');
  }

  let data;
  try {
    data = JSON.parse(message.data);
  } catch (e) {
    throw new Error('Invalid poll data');
  }
  if (!data.poll) {
    throw new Error('Message is not a poll');
  }

  // Already closed (early or expired) — no-op, return as-is.
  if (data.poll.closedAt) {
    return formatMessage(message);
  }
  data.poll.closedAt = Date.now();

  const updateParams = { data: JSON.stringify(data) };

  return new Promise((resolve, reject) => {
    rawChannel
      .updateUserMessage(messageId, updateParams)
      .then(updatedMessage => {
        resolve(formatMessage(updatedMessage));
      })
      .catch(reject);
  });
};

export default {
  CHANNEL_TYPES,
  initializeSendbird,
  getSendbirdInstance,
  getConnectionStatus,
  disconnectSendbird,
  updateUserProfile,
  // Channel collection (best practice)
  createChannelCollection,
  loadMoreChannels,
  disposeChannelCollection,
  // Message collection (best practice)
  createMessageCollection,
  loadPreviousMessages,
  loadNextMessages,
  disposeMessageCollection,
  // Direct operations
  getChannel,
  loadMessages,
  sendMessage,
  sendReply,
  getThreadReplies,
  markAsRead,
  getTotalUnreadCount,
  registerPushToken,
  // Typing
  startTyping,
  endTyping,
  // Archive/Delete/Leave
  archiveChannel,
  unarchiveChannel,
  leaveChannel,
  deleteChannel,
  isChannelFrozen,
  // File messages
  sendFileMessage,
  // Poll messages
  sendPollMessage,
  sendCtaMessage,
  voteOnPoll,
  closePoll,
  pollIsClosed,
  // Episode card messages
  sendEpisodeMessage,
  // Message reactions
  addReaction,
  deleteReaction,
  toggleReactionLocally,
};
